<script setup lang="ts">
import { widgetCopy } from '@/i18n/widget-copy'
import { nextTick, ref, useId, watch } from 'vue'
import { Info } from 'lucide-vue-next'
import type { ExpReadoutPresentation } from '@/ui/exp-readout-presentation'
import type { AppLocale } from '@/i18n/messages'

const props = defineProps<{ model?: ExpReadoutPresentation; locale: AppLocale; context?: string; iconOnly?: boolean; tooltip?: string }>()
const dialog = ref<HTMLDialogElement>()
const button = ref<HTMLButtonElement>()
const open = ref(false)
const id = useId()
const openedDescription = ref('')
async function show() {
  if (!props.model?.description) return
  openedDescription.value = props.model.description
  open.value = true
  await nextTick()
  if (open.value) dialog.value?.showModal()
}
function close() { dialog.value?.close(); open.value = false; button.value?.focus() }
watch(() => [props.context, props.locale], () => { if (open.value) close() })
</script>

<template>
  <div v-show="iconOnly || model?.label || open" class="exp-readout-status" :class="{ 'is-icon-only': iconOnly && !(model?.showLabel && model.label) }" data-exp-readout>
    <button ref="button" type="button" class="exp-readout-button" :class="{ 'is-empty': !model?.description }"
      :disabled="!model?.description" :tabindex="model?.description ? 0 : -1" :aria-hidden="!model?.description || undefined"
      :title="tooltip || model?.label || model?.rateLabel" :aria-description="tooltip"
      :aria-label="`${model?.label || model?.rateLabel || ''} · ${widgetCopy(locale, 'EXP reading details', '经验读数说明')}`"
      :aria-expanded="open" aria-haspopup="dialog" :aria-controls="id" @click="show">
      <span role="status" aria-atomic="true">{{ model?.label || '\u00a0' }}</span><Info aria-hidden="true" />
    </button>
    <dialog :id="id" ref="dialog" class="exp-readout-dialog" :aria-label="widgetCopy(locale, 'EXP reading details', '经验读数说明')"
      @close="open = false; button?.focus()" @cancel="close">
      <p class="exp-readout-dialog-caption">{{ widgetCopy(locale, 'Reading details when opened', '打开时的读数说明') }}</p>
      <p>{{ openedDescription }}</p>
      <button type="button" autofocus @click="close">{{ widgetCopy(locale, 'Close', '关闭') }}</button>
    </dialog>
  </div>
</template>
